import { useMemo } from 'react';
import { type SortDir } from '../../../../../models/SortDir';
import { colors } from '../../../../../themes';
import { ArrowDown, ArrowUp, Info } from '../../../../Icons';
import { Paragraph } from '../../../../Typography';
import { type ColumnType } from '../../types';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../Tooltip';
import {
  ActionWrapper,
  CellWrapper,
  Header,
  HeaderWrapper,
  IconWrapper,
  Label,
  SortIconWrapper,
  TooltipWrapper,
} from './style';

type HeaderCellProps = {
  tableSorted?: (colName: string, sortDir: SortDir) => void;
  onSort?: (fieldName: string) => void;
  sortDirection?: SortDir;
} & ColumnType;

const HeaderCell = ({
  name,
  colSpan,
  sortable,
  onSort,
  subLabel,
  tooltip,
  justify,
  sortDirection,
  fieldName,
}: HeaderCellProps) => {
  const sortIcon = useMemo(() => {
    if (sortDirection === 'ASC') {
      return (
        <SortIconWrapper>
          <ArrowUp />
        </SortIconWrapper>
      );
    }
    if (sortDirection === 'DESC') {
      return (
        <SortIconWrapper>
          <ArrowDown />
        </SortIconWrapper>
      );
    }

    return null;
  }, [sortDirection]);

  const tooltipMarkup = useMemo(() => {
    if (tooltip) {
      return (
        <TooltipWrapper>
          <Tooltip>
            <TooltipTrigger>
              <Info width="22px" height="22px" cursor="pointer" color={colors.primary.blue} />
            </TooltipTrigger>
            <TooltipContent>
              <Paragraph variant="medium">{tooltip}</Paragraph>
            </TooltipContent>
          </Tooltip>
        </TooltipWrapper>
      );
    }

    return null;
  }, [tooltip]);

  const canSort = !!onSort && !!sortable;

  return (
    <CellWrapper
      colSpan={colSpan}
      justify={justify}
      sortingDefined={canSort}
      onClick={
        canSort
          ? () => {
              onSort(fieldName);
            }
          : undefined
      }
    >
      <HeaderWrapper>
        <Header data-testid={`header-cell-${fieldName}`}>{name}</Header>
        <Label>{subLabel}</Label>
      </HeaderWrapper>
      <ActionWrapper>
        {(canSort && sortIcon && <IconWrapper>{sortIcon}</IconWrapper>) || null}
        {tooltipMarkup}
      </ActionWrapper>
    </CellWrapper>
  );
};

export default HeaderCell;
